import { CreditBalance, CreditPackage, CreditHistoryResponse, CreditTransaction } from '../types';
import { getToken } from './authApiClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Get authentication token from auth service
 */
const getAuthToken = (): string | null => {
  return getToken();
};

/**
 * Make authenticated API request
 */
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please login to continue.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get user's current credit balance
 */
export const getCreditBalance = async (): Promise<number> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/credits/balance`);

  if (!response.ok) {
    throw new Error('Failed to fetch credit balance');
  }

  const data = await response.json();
  return data.credits;
};

/**
 * Get user's credit transaction history
 */
/**
 * Get user's credit transaction history
 */
export const getCreditHistory = async (page: number = 1, pageSize: number = 20): Promise<CreditHistoryResponse> => {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/credits/history?page=${page}&pageSize=${pageSize}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch credit history');
  }

  return await response.json();
};

/**
 * Get available credit packages
 */
export const getCreditPackages = async (): Promise<CreditPackage[]> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/api/credits/packages`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch credit packages (${response.status})`);
    }

    const data = await response.json();
    return data.packages;
  } catch (error) {
    console.error('Error in getCreditPackages:', error);
    throw error;
  }
};

/**
 * Create Chargily Pay checkout session
 */
export const purchaseCredits = async (packageId: string): Promise<{
  success: boolean;
  checkoutUrl: string;
  checkoutId: string;
  amount: number;
  currency: string;
  message: string;
}> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/credits/purchase`, {
    method: 'POST',
    body: JSON.stringify({ packageId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create checkout session');
  }

  return await response.json();
};

/**
 * Admin: Add credits to a user
 */
export const adminAddCredits = async (userId: number, amount: number, reason?: string): Promise<{
  success: boolean;
  credits: number;
  transaction: CreditTransaction;
  message: string;
}> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/credits/admin/add`, {
    method: 'POST',
    body: JSON.stringify({ userId, amount, reason }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add credits');
  }

  return await response.json();
};

/**
 * Admin: Deduct credits from a user
 */
export const adminDeductCredits = async (userId: number, amount: number, reason?: string): Promise<{
  success: boolean;
  credits: number;
  transaction: CreditTransaction;
  message: string;
}> => {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/credits/admin/deduct`, {
    method: 'POST',
    body: JSON.stringify({ userId, amount, reason }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to deduct credits');
  }

  return await response.json();
};

/**
 * Admin: Get all users with their credit balances
 */
export const adminGetAllUserCredits = async (page: number = 1, pageSize: number = 50, searchTerm?: string): Promise<{
  users: Array<{
    id: number;
    username: string;
    email: string;
    role: string;
    credits: number;
    updatedAt: string;
  }>;
  total: number;
  page: number;
  pageSize: number;
}> => {
  let url = `${API_BASE_URL}/api/credits/admin/users?page=${page}&pageSize=${pageSize}`;
  if (searchTerm) {
    url += `&search=${encodeURIComponent(searchTerm)}`;
  }

  const response = await authenticatedFetch(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch user credits');
  }

  return await response.json();
};

/**
 * Admin: Get all transactions across all users
 */
export const adminGetAllTransactions = async (page: number = 1, pageSize: number = 50): Promise<{
  transactions: Array<CreditTransaction & { username: string; email: string }>;
  total: number;
  page: number;
  pageSize: number;
}> => {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/credits/admin/transactions?page=${page}&pageSize=${pageSize}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch transactions');
  }

  return await response.json();
};

